import type { Perimeter } from '@atlassian/atl-context';

import { CategoryStorageType } from '../../../types';

export enum Sources {
	BIFROST = 'bifrost',
	CLOUDFRONT_S3 = 'cloudfrontS3'
}

export enum FileType {
	COOKIE = 'COOKIE',
	LOCAL_STORAGE = 'LOCAL_STORAGE',
	SESSION_STORAGE = 'SESSION_STORAGE',
	INDEXED_DB = 'INDEXED_DB'
}

const generateResourcePath = (source: Sources, perimeter: string, storageType: string): string => {
	const baseCloudfrontS3CommercialPath =
		'https://atlassian-cookies--categories.us-east-1.prod.public.atl-paas.net/';
	const baseCloudfrontS3FedrampPath =
		'https://atlassian-cookies--categories.us-east-1.prod.cdn.atlassian-us-gov-mod.com/';

	const baseBifrostCommercialPath =
		'https://atlassian-cookies.prod-east.frontend.public.atl-paas.net/';
	const baseBifrostFedrampPath = 'https://atlassian-cookies.frontend.cdn.atlassian-us-gov-mod.com/';

	const fileName = `categories_${storageType}.json`;

	if (source === Sources.CLOUDFRONT_S3) {
		switch (perimeter) {
			case 'fedramp-moderate':
				return `${baseCloudfrontS3FedrampPath}${fileName}`;
			case 'commercial':
				return `${baseCloudfrontS3CommercialPath}${fileName}`;
			default:
				return `${baseCloudfrontS3CommercialPath}${fileName}`;
		}
	}

	switch (perimeter) {
		case 'fedramp-moderate':
			return `${baseBifrostFedrampPath}${fileName}`;
		case 'commercial':
			return `${baseBifrostCommercialPath}${fileName}`;
		default:
			return `${baseBifrostCommercialPath}${fileName}`;
	}
};

export const getCommercialCategoryCacheUrlLookup = (
	shouldUseBifrostSources: boolean,
): Record<CategoryStorageType, string> => {
	const distribution = shouldUseBifrostSources ? Sources.BIFROST : Sources.CLOUDFRONT_S3;
	return {
		[CategoryStorageType.Cookies]: generateResourcePath(distribution, 'commercial', FileType.COOKIE),
		[CategoryStorageType.LocalStorage]: generateResourcePath(
			distribution,
			'commercial',
			FileType.LOCAL_STORAGE
		),
		[CategoryStorageType.SessionStorage]: generateResourcePath(
			distribution,
			'commercial',
			FileType.SESSION_STORAGE,
		),
		[CategoryStorageType.IndexedDB]: generateResourcePath(distribution, 'commercial', FileType.INDEXED_DB),
	};
};

export const getCategoryCacheUrlPerimeterLookup = (
	shouldUseBifrostSources: boolean,
): Partial<Record<Perimeter, Record<CategoryStorageType, string>>> => {
	const distribution = shouldUseBifrostSources ? Sources.BIFROST : Sources.CLOUDFRONT_S3;
	return {
		commercial: getCommercialCategoryCacheUrlLookup(shouldUseBifrostSources),
		'fedramp-moderate': {
			[CategoryStorageType.Cookies]: generateResourcePath(distribution, 'fedramp-moderate', FileType.COOKIE),
			[CategoryStorageType.LocalStorage]: generateResourcePath(
				distribution,
				'fedramp-moderate',
				FileType.LOCAL_STORAGE
			),
			[CategoryStorageType.SessionStorage]: generateResourcePath(
				distribution,
				'fedramp-moderate',
				FileType.SESSION_STORAGE,
			),
			[CategoryStorageType.IndexedDB]: generateResourcePath(distribution, 'fedramp-moderate', FileType.INDEXED_DB),
		},
	};
};

// To be leveraged by SSR products
export const getCategoryCacheUrlPerimeterSSR = (
	source: Sources, perimeter: Perimeter, storageType: FileType
): string => generateResourcePath(source, perimeter, storageType);

